// /orgs/$orgSlug/projects/$projectId/shots/$shotId — LAYOUT route for
// the shot surface. The candidate grid itself lives in
// `…shots.$shotId.index.tsx`; the lineage viewer nests as
// `…shots.$shotId.lineage.$generationId.tsx`, so this parent must be a
// pure <Outlet/> (see orgs.$orgSlug.tsx for the dead-click rationale).
//
// `validateSearch` stays on the layout: the typed ShotSearch filter
// state belongs to the whole shot surface, so the lineage child keeps
// the filters in its URL and back-navigation to the grid restores them.

import { createFileRoute, Link, Outlet } from '@tanstack/react-router';

import { ApiError, apiErrorMessage } from '../api/client';
import { validateShotSearch } from '../components/filters';

export const Route = createFileRoute(
  '/orgs/$orgSlug/projects/$projectId/shots/$shotId'
)({
  validateSearch: validateShotSearch,
  component: Outlet,
  errorComponent: ShotRouteError,
});

function ShotRouteError({ error }: { error: unknown }): React.JSX.Element {
  const isApi = error instanceof ApiError;
  const status = isApi ? error.status : null;
  const heading =
    status === 404
      ? 'Shot not found'
      : status === 403
        ? 'You don’t have access to this shot'
        : 'Something went wrong loading this shot';
  const body = apiErrorMessage(error, 'Try again, or pick a different shot from the project page.');

  return (
    <div className="mx-auto max-w-2xl py-7">
      <p className="text-text-faint text-11 mb-2 uppercase tracking-wider">
        Error
      </p>
      <h1 className="text-text text-28 mb-3 font-semibold">{heading}</h1>
      <p className="text-text-muted mb-5 text-13">{body}</p>
      <Link
        to="/"
        className="bg-accent text-accent-fg border-border-strong rounded-2 hover:bg-accent-hi inline-block border px-3 py-2 text-13 transition-colors"
      >
        Back to home
      </Link>
    </div>
  );
}
